import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BookingService } from '../Service';
import { Box, Card, Chip, Divider, IconButton, Modal, Typography } from '@mui/material';
import { formatDateTime } from '../../../utils/dateUtils';
import { BookingDetailsContext } from './Viewdetails';
import { ZoomInMap, ZoomOutMap } from '@mui/icons-material';

const statusColor = {
    initiated: '#fb8c00',
    dialing: '#1565c0',
    answered: '#2e7d32',
    ended: '#6a1b9a',
    failed: '#d32f2f',
    completed: '#2e7d32',
};

const CallLogs = () => {
    const { bookingId } = useParams();
    const { bookingDetailsdata } = useContext(BookingDetailsContext);
    const [callLogs, setCallLogs] = useState([]);
    const [maximize, setMaximize] = useState(false);

    const fetchLogs = async () => {
        const res = await BookingService.getCallHistory(bookingId);
        if (res?.success === 1) {
            setCallLogs(res.data || []);
        }
    };

    useEffect(() => {
        if (bookingId > 0) {
            fetchLogs();
        }
    }, [bookingId, bookingDetailsdata]);

    return (
        <>
            <Card sx={{ mb: '2%' ,mt:'2%' }}>
                <Box sx={{ my: '1%', px: '2%', borderBottom: '1px solid #E0E0E0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: '1%', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Call Logs</Typography>
                        <IconButton size="small" onClick={() => setMaximize(true)}>
                            <ZoomOutMap sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ px: '2%', maxHeight: '12rem', minHeight: '4rem', overflowY: 'auto' }}>
                    {callLogs.length === 0 && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#9e9e9e', py: 1 }}>No calls yet</Typography>
                    )}
                    {callLogs.map((log, i) => (
                        <Box key={i} sx={{ py: 0.5, borderBottom: i < callLogs.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={log.target === 'provider' ? 'EXPERT' : 'CUSTOMER'}
                                    size="small"
                                    sx={{ fontSize: '0.55rem', fontWeight: 600, height: 18, bgcolor: log.target === 'provider' ? '#e3f2fd' : '#fce4ec', color: log.target === 'provider' ? '#1565c0' : '#c62828' }}
                                />
                                <Chip
                                    label={log.status}
                                    size="small"
                                    sx={{ fontSize: '0.55rem', fontWeight: 600, height: 18, bgcolor: `${statusColor[log.status]}18`, color: statusColor[log.status] }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                <Typography sx={{ fontSize: '0.6rem', color: '#424242', fontWeight: 500 }}>{log.customerName || log.providerName || log.customerPhone}</Typography>
                                {log.bookingNumber && <Typography sx={{ fontSize: '0.55rem', color: '#757575' }}>({log.bookingNumber})</Typography>}
                                {log.duration > 0 && (
                                    <Typography sx={{ fontSize: '0.55rem', color: '#757575' }}>{Math.floor(log.duration / 60)}m {log.duration % 60}s</Typography>
                                )}
                                <Typography sx={{ fontSize: '0.5rem', color: '#9e9e9e', ml: 'auto' }}>{formatDateTime(log.createdAt)}</Typography>
                            </Box>
                            <Typography sx={{ fontSize: '0.5rem', color: '#9e9e9e' }}>by {log.initiatedByName || `Admin #${log.initiatedBy}`}</Typography>
                            {log.status === 'failed' && log.errorDetails?.response?.details && (
                                <Typography sx={{ fontSize: '0.55rem', color: '#d32f2f', mt: 0.25 }}>{log.errorDetails.response.details}</Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Card>
            <Modal open={maximize} onClose={() => setMaximize(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: 600, bgcolor: 'background.paper', pt: 2, px: 4, pb: 3, borderRadius: '1rem',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>Call Logs</Typography>
                        <IconButton size="small" onClick={() => setMaximize(false)}>
                            <ZoomInMap sx={{ fontSize: '0.75rem' }} />
                        </IconButton>
                    </Box>
                    <Divider />
                    <Box sx={{ mt: 1, maxHeight: '60vh', overflowY: 'auto' }}>
                        {callLogs.length === 0 && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e', py: 2 }}>No calls yet</Typography>
                        )}
                        {callLogs.map((log, i) => (
                            <Box key={i} sx={{ py: 1, borderBottom: i < callLogs.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Chip label={log.target === 'provider' ? 'EXPERT' : 'CUSTOMER'} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, height: 22, bgcolor: log.target === 'provider' ? '#e3f2fd' : '#fce4ec', color: log.target === 'provider' ? '#1565c0' : '#c62828' }} />
                                    <Chip label={log.status} size="small" sx={{ fontSize: '0.65rem', fontWeight: 600, height: 22, bgcolor: `${statusColor[log.status]}18`, color: statusColor[log.status] }} />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.7rem' }}>
                                    <Typography sx={{ fontSize: '0.7rem' }}><b>Phone:</b> {log.customerPhone}</Typography>
                                    <Typography sx={{ fontSize: '0.7rem' }}><b>Name:</b> {log.customerName || log.providerName || '-'}</Typography>
                                    {log.bookingNumber && <Typography sx={{ fontSize: '0.7rem' }}><b>Booking:</b> {log.bookingNumber}</Typography>}
                                    {log.duration > 0 && <Typography sx={{ fontSize: '0.7rem' }}><b>Duration:</b> {Math.floor(log.duration / 60)}m {log.duration % 60}s</Typography>}
                                    <Typography sx={{ fontSize: '0.7rem' }}><b>Initiated:</b> {formatDateTime(log.initiatedAt)}</Typography>
                                    {log.answeredAt && <Typography sx={{ fontSize: '0.7rem' }}><b>Answered:</b> {formatDateTime(log.answeredAt)}</Typography>}
                                    {log.endedAt && <Typography sx={{ fontSize: '0.7rem' }}><b>Ended:</b> {formatDateTime(log.endedAt)}</Typography>}
                                    {log.recordingFilename && <Typography sx={{ fontSize: '0.7rem' }}><b>Recording:</b> {log.recordingFilename}</Typography>}
                                    {log.agentDetails?.agent_name && <Typography sx={{ fontSize: '0.7rem' }}><b>Agent:</b> {log.agentDetails.agent_name}</Typography>}
                                    {log.dispositions?.length > 0 && <Typography sx={{ fontSize: '0.7rem' }}><b>Disposition:</b> {log.dispositions.map(d => d.disposition_name || d).join(', ')}</Typography>}
                                    <Typography sx={{ fontSize: '0.7rem' }}><b>Initiated By:</b> {log.initiatedByName || `Admin #${log.initiatedBy}`}</Typography>
                                </Box>
                                {log.status === 'failed' && log.errorDetails && (
                                    <Box sx={{ mt: 0.5, p: 0.5, bgcolor: '#ffebee', borderRadius: 1 }}>
                                        <Typography sx={{ fontSize: '0.65rem', color: '#d32f2f' }}>{log.errorDetails.response?.details || log.errorDetails.message}</Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default CallLogs;
